import { useState } from "react";
import { ArrowRight, CircleAlert } from "lucide-react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { reportReactError } from "@/lib/report-react-error";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as Sentry from "@sentry/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

const feedbackSchema = z.object({
  message: z.string().trim().min(1, "Please describe what happened."),
  email: z
    .string()
    .trim()
    .email("Enter a valid email address.")
    .or(z.literal("")),
});

// Keep this independent of app providers and loader data: either may have failed.
export function CrashPage({ error, info }: ErrorComponentProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const form = useForm<z.infer<typeof feedbackSchema>>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { message: "", email: "" },
  });

  const onSubmit = (data: z.infer<typeof feedbackSchema>) => {
    setSubmitError(false);
    try {
      Sentry.captureFeedback(
        {
          message: data.message,
          email: data.email || undefined,
          url: window.location.href,
          associatedEventId: reportReactError(error, {
            componentStack: info?.componentStack,
          }),
        },
        { includeReplay: true },
      );
      setSubmitted(true);
    } catch {
      setSubmitError(true);
    }
  };

  return (
    <Empty className="min-h-dvh">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CircleAlert aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>
          <h1>Something went wrong</h1>
        </EmptyTitle>
        <EmptyDescription>
          Sorry, something went terribly wrong. Try reloading, or provide more
          information to help debug the issue.
        </EmptyDescription>
        <Collapsible className="w-full">
          <CollapsibleTrigger render={<Button variant="ghost" />} showCaret>
            Error details
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="bg-muted mt-2 max-h-64 overflow-auto rounded-lg p-4 text-left text-xs break-words whitespace-pre-wrap">
              {error.stack || `${error.name || "Error"}: ${error.message}`}
              {info?.componentStack
                ? `\n\nComponent stack:${info.componentStack}`
                : ""}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      </EmptyHeader>
      <EmptyContent>
        <div className="flex items-center gap-2">
          <Button onClick={() => window.location.reload()}>Reload page</Button>
          {submitted ? (
            <p role="status">Thanks for your feedback!</p>
          ) : (
            <Dialog>
              <DialogTrigger render={<Button variant="outline" />}>
                Send feedback
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send feedback</DialogTitle>
                  <DialogDescription>
                    Tell us what you were doing before the page crashed.
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex w-full flex-col gap-4 text-left"
                  >
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What happened?</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="What were you doing before this happened?"
                              rows={4}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email (optional)</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              autoComplete="email"
                              placeholder="you@example.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {submitError && (
                      <p role="alert">
                        Couldn’t submit your feedback. Please try again.
                      </p>
                    )}
                    <DialogFooter>
                      <Button
                        type="submit"
                        disabled={form.formState.isSubmitting}
                      >
                        Send feedback
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <a href="/" className="underline-link inline-flex items-center gap-1">
          Go home <ArrowRight className="size-4" aria-hidden="true" />
        </a>
      </EmptyContent>
    </Empty>
  );
}
